import { ResourceDetail, ResourceDetailsUpdate } from '@cibo/core'
import { DataGridPro, ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import { Box } from '@mui/material'
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRenderCellParams,
  GridRowHeightReturnValue,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro'
import { assocPath, difference, pathEq, propEq } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFieldColumns } from '../../../components/FieldColumns/useFieldColumns'
import { useBatchedFields, useUpdateMultipleFields } from '../../../queries'
import { BooleanDetailCheckboxCellRenderer } from './BooleanDetailCheckboxCellRenderer'

export type BulkAnswer = 'yes' | 'no' | 'unknown'

export type BooleanDetailSelectionTableProps<T extends ResourceDetail = ResourceDetail> = {
  detailKey?: keyof T['input'] | keyof T['result']
  answerValues?: Partial<Record<BulkAnswer, { value: any }>>
  rowSelectedAnswer?: 'yes' | 'no'
} & ResourceDetailFeatureTaskEditorProps<T>

export const BooleanDetailSelectionTable = <T extends ResourceDetail = ResourceDetail>({
  answerValues = { yes: { value: 'yes' }, no: { value: 'no' } },
  detailKey,
  rowSelectedAnswer = 'yes',
  detailRequirements: [{ traitId, year, resultsOnly }],
  onError,
  onSuccess,
  onUpdating,
  resourceIds,
}: Omit<BooleanDetailSelectionTableProps<T>, 'engagementId'>) => {
  const updateFields = useUpdateMultipleFields()
  const fieldModels = useBatchedFields(resourceIds)
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  })

  const { fieldNameColumn } = useFieldColumns()

  const columns = useMemo(() => {
    return [
      /*
       * Override the selection checkbox renderer to disable the checkbox when the detail
       * is immutable. As far as DataGrid is concerned, the row is still "selectable" so the
       * bulk checkbox could change it's selected status. Here, we control the rowSelectionModel
       * and weed out any selection changes for immutable rows.
       */
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        renderCell: (props: GridRenderCellParams) => (
          <BooleanDetailCheckboxCellRenderer {...props} traitId={traitId} year={year} />
        ),
      },
      fieldNameColumn,
    ]
  }, [])

  const rowDeselectedAnswer: BulkAnswer = rowSelectedAnswer === 'yes' ? 'no' : 'yes'

  const valuePath = useMemo(() => {
    const valuePath: Array<string> = [resultsOnly ? 'result' : 'input']
    if (detailKey) valuePath.push(detailKey as string)
    return valuePath
  }, [detailKey, resultsOnly])

  const detailHasAnswer = useCallback(
    (answer: BulkAnswer) => pathEq(valuePath, answerValues[answer]?.value),
    [valuePath, answerValues]
  )

  // init selections from saved details
  useEffect(() => {
    if (!fieldModels.isFetched) {
      return
    }

    const newSelectionModel = Array<string>()

    fieldModels.data?.forEach(fieldModel => {
      const detail = fieldModel.findDetail({ traitId, year })

      if (detail && detailHasAnswer(rowSelectedAnswer)(detail)) {
        newSelectionModel.push(fieldModel.resourceId)
      }
    })

    setRowSelectionModel({ type: 'include', ids: new Set(newSelectionModel) })
  }, [fieldModels.isFetched])

  const syncAnswers = (answers: Array<{ resourceId: string; answer: BulkAnswer }>) => {
    const template = { traitId, year }

    const updates: ResourceDetailsUpdate[] = []

    answers.forEach(({ resourceId, answer }) => {
      const fieldModel = fieldModels.data?.find(propEq('resourceId', resourceId))
      if (!fieldModel) return

      const detail = fieldModel?.findDetail({ traitId, year })

      if (detail?.immutable) return

      if (!detailHasAnswer(answer)(detail)) {
        updates.push({
          resourceId: fieldModel.resourceId,
          details: [assocPath(valuePath, answerValues?.[answer]?.value, template)],
        })
      }
    })

    if (updates.length === 0) return

    onUpdating?.()
    updateFields
      .mutateAsync(updates)
      .then(() => {
        fieldModels.refetch().then(onSuccess)
      })
      .catch(onError)
  }

  const fieldIds = useMemo(() => fieldModels.data?.map(fieldModel => fieldModel.resourceId) ?? [], [
    fieldModels.data,
  ])

  const onRowSelectionModelChange = (newSelectionModel: GridRowSelectionModel) => {
    const selectionModelIds = Array.from(newSelectionModel.ids)

    // DataGrid will up and change the model to "exclude" in some cases.
    const includedIds =
      newSelectionModel.type === 'include'
        ? selectionModelIds
        : difference(fieldIds, selectionModelIds)

    const answers: Array<{ resourceId: string; answer: BulkAnswer }> = []

    fieldModels.data?.forEach(fieldModel => {
      const detail = fieldModel.findDetail({ traitId, year })

      if (!detail?.immutable) {
        answers.push({
          resourceId: fieldModel.resourceId,
          answer: includedIds.includes(fieldModel.resourceId)
            ? rowSelectedAnswer
            : rowDeselectedAnswer,
        })
      }
    })

    setRowSelectionModel({ ids: new Set(includedIds), type: 'include' })

    syncAnswers(answers)
  }

  const getRowHeight = useCallback((): GridRowHeightReturnValue => 'auto', [])

  return (
    <Box sx={{ flex: 'flex', flexDirection: 'column' }}>
      <DataGridPro
        columns={columns}
        loading={fieldModels.isPending || updateFields.isPending}
        rows={fieldModels.data ?? []}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        getRowHeight={getRowHeight}
        hideCellFocus
      />
    </Box>
  )
}
